import { AddCenterImageState } from './AddCenterImage.types'
import {
  DEFAULT_FLOWCODE_LOGO_DIMENSIONS,
  FILE_NAME_LENGTH_ERROR,
  FILE_UPLOAD_SIZE_ERROR,
  MAX_FILE_NAME_LENGTH,
  MAX_FILE_UPLOAD_SIZE
} from '../../../../constants/flowcode'
import { DISABLE_AUTO_DESIGN_LOGO_RESIZE } from '@dtx-company/true-common/src/constants/featureFlags'
import { FcGeneratorOptions } from '@dtx-company/flowcode-generator-browser'
import {
  clearFlowcodeCenterImage as clearCenterImageState,
  setFlowcodeCenterImage as updateCenterImage
} from '../../../../redux/slices/createFlowcodeSlice'
import { fcAutoScaleLogo } from '@dtx-company/flowcode-generator-browser/src/fcAutoscaleLogo'
import { getFlowcodeLogoDimensions } from '@app/code/src/utils/flowcode'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { updateCenterImageInCodePreview } from '@dtx-company/inter-app/src/redux/slices/homePageSlice'
import { useCallback, useEffect } from 'react'
import { useCreateFlowcodeState } from '../../../../hooks/useCreateFlowcodeState'
import { useCreateFlowcodeStateActions } from '../CreateFlowcodeForm.hooks'
import { useDispatch } from 'react-redux'
import { useFlowFeature } from '@dtx-company/inter-app/src/hooks/useFlowFeature/useFlowFeature'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowcode'

export function useAddCenterImageState(arg?: { variant?: string | null }): AddCenterImageState {
  const isMounted = useIsMounted()
  const { centerImage: centerImageFile, options, template } = useCreateFlowcodeState()
  const dispatch = useDispatch()
  const { setRotate } = useCreateFlowcodeStateActions()
  const [disableLogoResize] = useFlowFeature(DISABLE_AUTO_DESIGN_LOGO_RESIZE)
  const setCenterImage = useCallback(
    async (file: File, externalCenterImageUrl?: string): Promise<void> => {
      if (file?.name && file?.name.length > MAX_FILE_NAME_LENGTH) {
        return sendErrorNotification(FILE_NAME_LENGTH_ERROR.message)
      }
      if (file?.size && file?.size > MAX_FILE_UPLOAD_SIZE) {
        return sendErrorNotification(FILE_UPLOAD_SIZE_ERROR.message)
      }
      const objectUrl = externalCenterImageUrl ?? URL.createObjectURL(file)
      const opts: FcGeneratorOptions = {
        ...options,
        logoImageUrl: objectUrl
      }
      const {
        hasLogoResized,
        options: { logoHeight: autoScaledHeight, logoWidth: autoScaledWidth, qrrotate } = {}
      } = await fcAutoScaleLogo(opts, undefined, true, false)
      const intitialRotate = template?.options?.qrrotate ?? false
      if (typeof qrrotate === 'boolean' && qrrotate !== intitialRotate) {
        setRotate(qrrotate)
      } else {
        setRotate(null)
      }

      let logoHeight = autoScaledHeight
      let logoWidth = autoScaledWidth

      // fallback to legacy resize if either dimension is 0
      // fallback to legacy resize if auto scale util did not scale the logo
      if ((disableLogoResize || logoHeight === 0 || logoWidth === 0 || !hasLogoResized) && !!file) {
        const { height, width } = await getFlowcodeLogoDimensions(file)
        logoHeight = height
        logoWidth = width
      }

      dispatch(
        updateCenterImage({
          name: file?.name ?? '',
          height: logoHeight ?? DEFAULT_FLOWCODE_LOGO_DIMENSIONS.HEIGHT,
          width: logoWidth ?? DEFAULT_FLOWCODE_LOGO_DIMENSIONS.WIDTH,
          objectUrl
        })
      )
      events.userAddedCenterFlowcodeImage(false, arg?.variant)
    },
    [dispatch, arg?.variant, options, setRotate, template, disableLogoResize]
  )
  const clearCenterImage = useCallback((): void => {
    URL.revokeObjectURL(centerImageFile.objectUrl)
    dispatch(clearCenterImageState())
    setRotate(null)
  }, [dispatch, centerImageFile.objectUrl, setRotate])

  useEffect(() => {
    if (isMounted()) {
      dispatch(updateCenterImageInCodePreview(centerImageFile))
    }
  }, [dispatch, centerImageFile, isMounted])

  return {
    fileName: centerImageFile?.name ?? '',
    setCenterImage,
    clearCenterImage
  }
}
